<script>
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import { onMounted } from "vue";
import { useEnrollmentStore } from "@/stores/access/enrollment-store.js";
import { useAuthStore } from "@/stores/auth-store";
import { useSharedStore } from "@/stores/shared-store";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";

export default {
  components: {
    BaseLoading,
  },
  props: {
    courseID: {
      type: [String, Number],
    },
  },
  setup(props) {
    const courseFetchStore = useCourseFetchStore();
    const enrollmentStore = useEnrollmentStore();
    const authStore = useAuthStore();
    const sharedStore = useSharedStore();

    onMounted(() => {
      const studentId = authStore.currentUser?.id;
      if (studentId) {
        enrollmentStore.fetchEnrollmentStudentList(studentId);
      }
    });
    return {
      courseFetchStore,
      enrollmentStore,
      authStore,
      sharedStore,
    };
  },
  computed: {
    hasResumeCourses() {
      return (this.enrollmentStore.enrollmentStudent || []).length > 0;
    },
  },
  methods: {
    resolveCourseId(item) {
      const course = item?.courseID;
      if (course && typeof course === "object") {
        return course.course_id ?? course.id ?? null;
      }
      return course ?? null;
    },
    handleResume(item) {
      const courseID = this.resolveCourseId(item);
      const enrollmentID = item?.enrollmentID;
      if (!courseID) {
        this.sharedStore.showError(
          "Course tidak ditemukan. Silakan refresh halaman."
        );
        return;
      }

      // Seed detail dari data enrollment supaya watch tidak kosong
      // kalau fetch manage/:id gagal / lambat.
      if (item?.courseID && typeof item.courseID === "object") {
        this.courseFetchStore.setBasicCourseDetail(item.courseID);
      }

      const role = this.authStore.role?.toLowerCase();
      if (role !== "principal" && role !== "teacher" && !enrollmentID) {
        this.sharedStore.showError(
          "Data enrollment tidak ditemukan. Silakan refresh halaman."
        );
        return;
      }

      const slug =
        role === "principal" || role === "teacher"
          ? btoa(`${courseID}`)
          : btoa(`${courseID}-${enrollmentID}`);

      this.$router.push({
        name: "CourseWatch",
        params: { slug },
      });
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Course Resume</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Course Resume
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <!-- Course detail -->
  <div class="course-resume">
    <div class="container">
      <template v-if="!sharedStore.loading">
        <!-- Empty state -->
        <div
          v-if="!hasResumeCourses"
          class="text-center p-5 bg-light border rounded mb-4 course-resume-empty"
        >
          <div
            class="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border mb-3"
            style="width: 72px; height: 72px"
            aria-hidden="true"
          >
            <i class="isax isax-pause-circle5 fs-32 text-secondary"></i>
          </div>
          <h5 class="mb-2">Belum ada course untuk dilanjutkan</h5>
          <p class="mb-3 text-muted mx-auto" style="max-width: 28rem">
            Daftar course dulu, lalu lanjutkan progress-mu dari sini.
          </p>
          <router-link
            to="/course/course-grid"
            class="btn btn-secondary d-inline-flex align-items-center"
          >
            <i class="isax isax-search-normal-14 me-1"></i>
            Cari Course
          </router-link>
        </div>

        <!-- Enrolled courses -->
        <template v-else>
          <div
            class="card mb-4"
            v-for="(item, index) in enrollmentStore.enrollmentStudent"
            :key="item.enrollmentID || item.courseID?.course_id || index"
          >
            <div class="card-body">
              <div
                class="course-resume-details-1 d-lg-flex align-items-center mb-3"
              >
                <div class="position-relative cou-resume-image">
                  <img
                    class="img-fluid"
                    :src="`${authStore.Url}/media/${item.courseID.thumbnail}`"
                    alt="img"
                  />
                </div>
                <div class="cou-resume-content w-100 ps-lg-4">
                  <h3 class="mb-2">
                    {{ item.courseID.title }}
                  </h3>
                  <p class="mb-3 fs-14">
                    {{ item.courseID.short_description }}
                  </p>
                  <div class="d-flex align-items-center cou-lesson">
                    <p class="d-flex align-items-center">
                      <img
                        class="img-fluid"
                        src="@/assets/img/icons/book3.svg"
                        alt="img"
                      />{{ item.courseID.content_count }} Lesson
                    </p>
                    <p class="d-flex align-items-center">
                      <img
                        class="img-fluid"
                        src="@/assets/img/icons/timer-start3.svg"
                        alt="img"
                      />{{ sharedStore.formatTanggal(item.expiredAt) }}
                    </p>
                    <span
                      class="badge badge-sm fs-12 rounded-pill bg-warning"
                      v-if="item.status == 'Ongoing'"
                      >{{ item.status }}</span
                    >
                    <span
                      class="badge badge-sm fs-12 rounded-pill bg-success"
                      v-if="item.status == 'Completed'"
                      >{{ item.status }}</span
                    >
                    <span
                      class="badge badge-sm fs-12 rounded-pill bg-danger"
                      v-if="item.status == 'Remedial'"
                      >{{ item.status }}</span
                    >
                    <span
                      class="badge badge-sm fs-12 rounded-pill bg-danger"
                      v-if="item.status == 'Expired'"
                      >{{ item.status }}</span
                    >
                    <span
                      class="badge badge-sm fs-12 rounded-pill bg-primary"
                      v-if="item.status == 'Grading'"
                      >{{ item.status }}</span
                    >
                  </div>
                  <button
                    type="button"
                    class="btn btn-secondary d-inline-flex align-items-center"
                    @click="handleResume(item)"
                  >
                    <i class="isax isax-pause-circle5 me-1"></i> Resume Course
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
  <!-- /Course detail -->
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
  <div style="height: 50px">
    <footer-two></footer-two>
  </div>
</template>

<style scoped>
.course-resume-empty {
  animation: courseResumeEmptyIn 0.35s ease-out;
}

@keyframes courseResumeEmptyIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .course-resume-empty {
    animation: none;
  }
}
</style>
